import React, { useState } from 'react'
import { Modal, message } from 'antd'
import { Button } from '@surecompanies/core_components'
import { useQueryClient } from '@tanstack/react-query'
import WarningOutlined from '@ant-design/icons/WarningOutlined'
import RequirePermission from 'enrollment-platform/rbac/RequirePermission'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { InvoicePaymentAttemptDto } from '../../api/dto/invoice-delivery.dto'
import {
  INVOICE_DELIVERY_QUERY_KEY,
  STATS_QUERY_KEY,
} from '../../api/invoice-delivery-api-client'
import { useRetryStripePaymentIntentMutation } from '../../api/stripe-payment-api-client'
import { formatCurrency, formatStatusLabel } from './generation/utils'
import {
  NOT_STARTED_STATUS,
  canRetryPaymentAttempt,
  centsToDollars,
  resolveBankDetailLabel,
  resolveRetryBankDetailLabel,
  resolveRetryPaymentMethodId,
  resolveStatusTone,
} from './paymentStatus'
import { DrawStatusBadge } from './InvoiceDeliveryTableWithStripePayment.styles'
import { StripePaymentEventHistory } from './StripePaymentEventHistory'
import {
  DrawBySubCompanyAmount,
  DrawBySubCompanyBankLine,
  DrawBySubCompanyCard,
  DrawBySubCompanyErrorMessage,
  DrawBySubCompanyId,
  DrawBySubCompanyInfo,
  DrawBySubCompanyList,
  DrawBySubCompanyMeta,
  DrawBySubCompanyName,
  DrawBySubCompanyRow,
  DrawBySubCompanyStatus,
} from './InvoiceDeliveryDrawer.styles'

const FAILED_PAYMENT_STATUS = 'failed'
/* Shown on a failed attempt once its retry has been accepted. */
const RETRY_SUBMITTED_STATUS = 'processing'

const RETRY_FAILED_FALLBACK_MESSAGE = 'Retry failed. Please try again.'

/* Prefer the API's own message over axios's generic "Request failed with status code …". */
const resolveRetryErrorMessage = (error: unknown): string => {
  const apiMessage = (
    error as { response?: { data?: { message?: string | string[] } } } | null
  )?.response?.data?.message
  if (Array.isArray(apiMessage) && apiMessage.length > 0)
    return apiMessage.join(', ')
  if (typeof apiMessage === 'string' && apiMessage) return apiMessage
  return (error as Error)?.message || RETRY_FAILED_FALLBACK_MESSAGE
}

const isFiniteAmount = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

interface DrawBySubCompanySectionProps {
  paymentList: InvoicePaymentAttemptDto[]
  /** False for archived/historical deliveries — failed attempts show no Retry. */
  canRetry?: boolean
}

const DrawBySubCompanySection: React.FC<DrawBySubCompanySectionProps> = ({
  paymentList,
  canRetry = true,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync: retryPaymentIntent } =
    useRetryStripePaymentIntentMutation()
  /*
   * The drawer renders a snapshot of the delivery that a refetch doesn't
   * replace, so a retried attempt would keep reading `failed` and offer Retry
   * again. Remember what was retried here and show it as `processing` instead,
   * which also hides Retry so a second click can't charge twice.
   */
  const [retriedIntentIds, setRetriedIntentIds] = useState<Set<string>>(
    () => new Set()
  )

  const handleRetry = (attempt: InvoicePaymentAttemptDto) => {
    const paymentIntentId = attempt.stripePaymentIntentId
    const paymentMethod = resolveRetryPaymentMethodId(attempt)
    if (!paymentIntentId || !paymentMethod) return

    const bankDetailLabel = resolveRetryBankDetailLabel(attempt)
    const amountLabel = isFiniteAmount(attempt.paymentAmount)
      ? formatCurrency(centsToDollars(attempt.paymentAmount))
      : null

    Modal.confirm({
      title: 'Retry failed payment?',
      content: (
        <div>
          This retries the failed Stripe payment
          {amountLabel && (
            <>
              {' '}
              of <strong>{amountLabel}</strong>
            </>
          )}
          {bankDetailLabel && <> from {bankDetailLabel}</>}.
        </div>
      ),
      okText: 'Retry',
      onOk: async () => {
        try {
          await retryPaymentIntent({
            paymentIntentId,
            body: { paymentMethod },
          })
        } catch (error) {
          // Resolve without rethrowing so Modal.confirm closes; the toast
          // carries the outcome and the attempt stays `failed` with Retry.
          message.error(resolveRetryErrorMessage(error))
          return
        }
        message.success('Payment retry submitted.')
        setRetriedIntentIds((prev) => new Set(prev).add(paymentIntentId))
        // Not awaited: a paused refetch would otherwise hold the dialog open.
        void queryClient.invalidateQueries({
          queryKey: [INVOICE_DELIVERY_QUERY_KEY],
        })
        void queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      },
    })
  }

  return (
    <DrawBySubCompanyList>
      {paymentList.map((attempt, index) => {
        const isRetried =
          !!attempt.stripePaymentIntentId &&
          retriedIntentIds.has(attempt.stripePaymentIntentId)
        const status =
          isRetried && attempt.paymentStatus === FAILED_PAYMENT_STATUS
            ? RETRY_SUBMITTED_STATUS
            : attempt.paymentStatus
        const label =
          !status || status === NOT_STARTED_STATUS
            ? 'Not Started'
            : formatStatusLabel(status)
        const tone = resolveStatusTone(status ?? NOT_STARTED_STATUS)
        const bankDetailLabel = resolveBankDetailLabel(attempt)
        const showRetry =
          canRetry && !isRetried && canRetryPaymentAttempt(attempt)

        return (
          <DrawBySubCompanyCard
            key={
              attempt.stripeCustomerId ??
              `${attempt.stripePaymentIntentId}-${index}`
            }
          >
            <DrawBySubCompanyRow>
              <DrawBySubCompanyInfo>
                {attempt.stripeBillingProfileDescription && (
                  <DrawBySubCompanyName>
                    {attempt.stripeBillingProfileDescription}
                  </DrawBySubCompanyName>
                )}
                <DrawBySubCompanyBankLine>
                  <DrawBySubCompanyName
                    $isWarning={!bankDetailLabel}
                    title={bankDetailLabel ?? undefined}
                  >
                    {!bankDetailLabel ? (
                      <>
                        <WarningOutlined />
                        No payment method
                      </>
                    ) : (
                      bankDetailLabel
                    )}
                  </DrawBySubCompanyName>
                  {showRetry && (
                    <RequirePermission
                      action={RbacActions.Draw}
                      subject={RbacSubjects.FundingDraw}
                    >
                      <Button size="small" onClick={() => handleRetry(attempt)}>
                        Retry
                      </Button>
                    </RequirePermission>
                  )}
                </DrawBySubCompanyBankLine>
                {attempt.stripeCustomerId && (
                  <DrawBySubCompanyId>
                    {attempt.stripeCustomerId}
                    {attempt.stripeAccount && ` (${attempt.stripeAccount})`}
                  </DrawBySubCompanyId>
                )}
              </DrawBySubCompanyInfo>

              <DrawBySubCompanyMeta>
                <DrawBySubCompanyStatus>
                  <DrawStatusBadge $tone={tone}>{label}</DrawStatusBadge>
                  <DrawBySubCompanyAmount>
                    {isFiniteAmount(attempt.paymentAmount)
                      ? formatCurrency(centsToDollars(attempt.paymentAmount))
                      : '—'}
                  </DrawBySubCompanyAmount>
                </DrawBySubCompanyStatus>
              </DrawBySubCompanyMeta>
            </DrawBySubCompanyRow>

            {status === FAILED_PAYMENT_STATUS &&
              attempt.stripePaymentErrorMessage && (
                <DrawBySubCompanyErrorMessage>
                  <WarningOutlined />
                  {attempt.stripePaymentErrorMessage}
                </DrawBySubCompanyErrorMessage>
              )}

            <StripePaymentEventHistory events={attempt.stripePaymentEvents} />
          </DrawBySubCompanyCard>
        )
      })}
    </DrawBySubCompanyList>
  )
}

export { DrawBySubCompanySection }
